import { createFileRoute, ErrorComponent, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { useQuery, queryOptions } from "@tanstack/react-query";
import { fetchPackages, fetchServices } from "@/lib/queries";
import { trackViewItemList } from "@/lib/analytics";

const packagesQueryOptions = queryOptions({ queryKey: ["packages"], queryFn: fetchPackages });
const servicesQueryOptions = queryOptions({ queryKey: ["services"], queryFn: fetchServices });
import { Button } from "@/components/ui/button";
import { money } from "@/lib/format";
import { Check, Plus, Package as PackageIcon, Sparkles } from "lucide-react";
import { useCart, isIncluded } from "@/lib/cart";
import { Disclaimer } from "@/components/site/Disclaimer";
import { getPackageStyle } from "@/lib/package-styles";
import { useMemo } from "react";
import { pageMeta, jsonLdScriptTag, SITE_BASE_URL } from "@/lib/seo";

export const Route = createFileRoute("/paquetes")({
  head: () =>
    pageMeta({
      path: "/paquetes",
      title: "Paquetes — Truck Company Center | Car Carriers en Florida",
      description:
        "Compara 3 paquetes para abrir tu trucking company en Florida: LLC, EIN, USDOT, MC, BOL, W-9, Central Dispatch y Super Dispatch.",
      ogTitle: "Paquetes — Truck Company Center",
      ogDescription:
        "Tres paquetes para lanzar tu trucking company. Ahorro frente a pedirlo por separado.",
    }),
  loader: async ({ context: { queryClient } }) => {
    await Promise.all([
      queryClient.ensureQueryData(packagesQueryOptions),
      queryClient.ensureQueryData(servicesQueryOptions),
    ]);
  },
  errorComponent: ({ error }) => <ErrorComponent error={error} />,
  component: PaquetesPage,
});

// Color del Plan 2 (Operación Lista) — usado en P3 para resaltar servicios
// heredados de Operación Lista en la card de Lanzamiento Premium.
const P2_INHERITED_ICON = getPackageStyle("operacion-lista").iconClass;

function PaquetesPage() {
  const { data: packages = [], isLoading } = useQuery(packagesQueryOptions);
  const { data: services = [] } = useQuery(servicesQueryOptions);
  const cart = useCart();

  useEffect(() => {
    if (packages.length === 0) return;
    trackViewItemList(
      "paquetes",
      packages.map((p) => ({
        id: p.id,
        slug: p.slug,
        name: p.name,
        price: Number(p.price),
        type: "package" as const,
      }))
    );
  }, [packages]);

  const svcMap = new Map(services.map((s) => [s.id, s]));
  const sortedPackages = [...packages].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  const jsonLdPackages = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Paquetes — Truck Company Center",
      itemListElement: sortedPackages.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Service",
          name: p.name,
          description: p.description ?? undefined,
          provider: {
            "@type": "Organization",
            name: "Truck Company Center",
            url: SITE_BASE_URL,
          },
          offers: {
            "@type": "Offer",
            price: Number(p.price).toFixed(2),
            priceCurrency: "USD",
            url: `${SITE_BASE_URL}/paquetes`,
          },
        },
      })),
    }),
    [sortedPackages],
  );

  return (
    <div>
      {sortedPackages.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: jsonLdScriptTag(jsonLdPackages) }}
        />
      )}
      <section className="border-b border-hairline bg-radial-fade">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="text-xs uppercase tracking-[0.2em] text-label-accent">Paquetes</div>
          <h1 className="mt-3 font-display text-4xl font-semibold text-foreground md:text-5xl">Elige cómo quieres empezar</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Tres formas de empezar. Cada paquete sale más barato que pedir los servicios por separado.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        {isLoading ? (
          <div className="text-muted-foreground">Cargando paquetes...</div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-3">
            {sortedPackages.map((p, i) => {
              const style = getPackageStyle(p.slug);
              const inCart = cart.items.some((it) => it.type === "package" && it.id === p.id);
              const baseIds = new Set(sortedPackages[0]?.service_ids ?? []);
              const prevIds = new Set(i > 0 ? (sortedPackages[i - 1]?.service_ids ?? []) : []);

              return (
                <div key={p.id} className={`relative flex flex-col rounded-xl border bg-surface p-6 ${style.cardBorder}`}>
                  {style.ribbon && (
                    <div className={`absolute -top-3 left-6 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wider ${style.ribbon.class}`}>
                      {style.ribbon.label}
                    </div>
                  )}
                  <div className="flex flex-col lg:min-h-[12.5rem]">
                    <div className="flex items-center gap-2">
                      <PackageIcon className={`h-4 w-4 ${style.iconClass}`} />
                      <span className={`text-xs uppercase tracking-wider ${style.badgeText}`}>{p.name}</span>
                    </div>
                    <div className="mt-4 flex items-baseline gap-2">
                      <span className="font-display text-4xl font-semibold text-foreground">{money(p.price)}</span>
                      <span className="text-sm text-muted-foreground line-through">{money(p.original_value)}</span>
                    </div>
                    <div className="mt-1 inline-flex w-fit rounded bg-success/15 px-2 py-0.5 text-[11px] font-medium text-[oklch(0.78_0.16_155)]">
                      Ahorras {money(p.savings)}
                    </div>
                    <p className="mt-4 text-sm text-muted-foreground">{p.description}</p>
                  </div>

                  <div className="mt-5 border-t border-hairline pt-5">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Incluye</div>
                    <ul className="mt-3 space-y-2">
                      {[...p.service_ids]
                        .map((sid) => svcMap.get(sid))
                        .filter((s): s is NonNullable<typeof s> => !!s)
                        .sort((a, b) => a.sort_order - b.sort_order)
                        .map((s) => {
                          // En P1 (Base Florida) todo es color base. En P2/P3 distinguimos heredado vs nuevo.
                          let iconCls = style.iconClass;
                          let textCls = "text-foreground";
                          let badge: { label: string; cls: string } | null = null;

                          if (i === 0) {
                            iconCls = style.iconClass;
                            textCls = "text-foreground";
                          } else if (baseIds.has(s.id)) {
                            // Heredado de Base
                            iconCls = "text-muted-foreground";
                            textCls = "text-muted-foreground";
                          } else if (i === 2 && prevIds.has(s.id)) {
                            // En P3, heredado de P2 → color del P2
                            iconCls = P2_INHERITED_ICON;
                            textCls = "text-foreground";
                          } else {
                            // Nuevo de este plan
                            iconCls = style.iconClass;
                            textCls = "text-foreground font-medium";
                            badge = { label: "Nuevo", cls: `${style.accentBg} ${style.badgeText}` };
                          }

                          return (
                            <li key={s.id} className={`flex items-start gap-2 text-sm ${textCls}`}>
                              <Check className={`mt-0.5 h-4 w-4 shrink-0 ${iconCls}`} />
                              <span className="flex-1">{s.name}</span>
                              {badge && (
                                <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${badge.cls}`}>
                                  {badge.label}
                                </span>
                              )}
                            </li>
                          );
                        })}
                    </ul>
                  </div>

                  {p.bonus_service_ids?.length > 0 && (
                    <div className="mt-4 rounded-md border border-secondary/30 bg-secondary/5 p-3">
                      <div className="text-[11px] uppercase tracking-wider text-label-accent">Bonus incluidos</div>
                      <ul className="mt-2 space-y-1.5">
                        {[...p.bonus_service_ids]
                          .map((sid) => svcMap.get(sid))
                          .filter((s): s is NonNullable<typeof s> => !!s)
                          .sort((a, b) => a.sort_order - b.sort_order)
                          .map((s) => (
                            <li key={s.id} className="flex items-start gap-2 text-sm text-foreground">
                              <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-secondary" />
                              <span>{s.name}</span>
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}

                  <Button
                    className={`mt-6 ${
                      inCart
                        ? "bg-success text-success-foreground hover:bg-success/90"
                        : style.buttonClass
                    }`}
                    onClick={() =>
                      cart.addPackage({
                        id: p.id, slug: p.slug, name: p.name,
                        price: Number(p.price),
                        includedServiceIds: [...p.service_ids, ...(p.bonus_service_ids ?? [])],
                      })
                    }
                  >
                    {inCart ? "En tu carrito ✓" : `Elegir ${p.name}`}
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {/* Add extras */}
        <div className="mt-14 rounded-xl border border-hairline bg-surface/60 p-6 md:p-8">
          <div className="flex flex-col items-start justify-between gap-3 md:flex-row md:items-end">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-label-accent">Agregar extras</div>
              <h2 className="mt-2 font-display text-2xl font-semibold text-foreground">Suma servicios opcionales a tu paquete</h2>
              <p className="mt-1 text-sm text-muted-foreground">Lo que ya esté incluido en tu paquete se marcará como "Incluido".</p>
            </div>
            <Link to="/servicios">
              <Button variant="outline" className="border-hairline bg-surface hover:bg-surface-2">Ver todos los servicios</Button>
            </Link>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {services.slice(0, 9).map((s) => {
              const included = isIncluded(s.id, cart.items);
              const inCart = cart.items.some((it) => it.type === "service" && it.id === s.id);
              return (
                <div key={s.id} className="flex items-center justify-between gap-3 rounded-md border border-hairline bg-surface p-3">
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wider text-label-accent">{s.category}</div>
                    <div className="truncate text-sm font-medium text-foreground">{s.name}</div>
                    <div className="text-xs text-muted-foreground">{money(s.price)}</div>
                  </div>
                  {included ? (
                    <span className="rounded bg-success/15 px-2 py-1 text-[11px] font-medium text-[oklch(0.78_0.16_155)]">Incluido</span>
                  ) : (
                    <Button size="sm" variant="outline" className="border-hairline"
                      disabled={inCart}
                      onClick={() => cart.addService({ id: s.id, slug: s.slug, name: s.name, price: Number(s.price) })}>
                      {inCart ? "Añadido" : <><Plus className="mr-1 h-3 w-3" />Añadir</>}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-10">
          <Disclaimer />
        </div>
      </section>
    </div>
  );
}
